import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { useToast } from '../hooks/use-toast';
import { Send, Loader2, CheckCircle2 } from 'lucide-react';
import { applicationsApi } from '../utils/api';

const validateEmail = (email) => /^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(email);

const emptyForm = { firstName: '', lastName: '', email: '', message: '' };

export const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [consent, setConsent] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const { toast } = useToast();

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName || formData.firstName.length < 2) newErrors.firstName = 'Adınızı daxil edin';
    if (!formData.lastName || formData.lastName.length < 2) newErrors.lastName = 'Soyadınızı daxil edin';
    if (!formData.email) newErrors.email = 'Elektron ünvanı daxil edin';
    else if (!validateEmail(formData.email)) newErrors.email = 'Format: ad@nümunə.az';
    if (!formData.message || formData.message.length < 5) newErrors.message = 'Mesajınızı daxil edin';
    if (!consent) newErrors.consent = 'Müraciəti göndərmək üçün şəxsi məlumatların emalına razılıq tələb olunur';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      await applicationsApi.create({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        productType: 'Ümumi sual',
        message: formData.message
      });
      setSubmitted(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMessage = "Müraciət göndərilə bilmədi. Xahiş edirik yenidən cəhd edin.";
      if (typeof detail === 'string') errorMessage = detail;
      else if (Array.isArray(detail)) errorMessage = detail.map(d => d.msg || '').filter(Boolean).join('; ') || errorMessage;
      toast({ title: "Xəta baş verdi", description: errorMessage, variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === 'email' ? value.trim().toLowerCase() : value;
    setFormData({ ...formData, [name]: nextValue });
    if (errors[name]) setErrors({ ...errors, [name]: '' });
  };

  const handleReset = () => {
    setSubmitted(false);
    setFormData(emptyForm);
    setConsent(false);
    setErrors({});
  };

  if (submitted) {
    return (
      <div className="text-center py-10" data-testid="success-state">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 mx-auto mb-5">
          <CheckCircle2 className="h-8 w-8 text-green-600" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-3">Mesajınız alındı!</h3>
        <p className="text-base text-gray-600 max-w-md mx-auto mb-6">
          Təşəkkür edirik. Mütəxəssisimiz <strong className="text-gray-900">1 iş günü ərzində</strong> sizinlə
          bu ünvan üzərindən əlaqə saxlayacaq: <strong className="text-gray-900">{formData.email}</strong>
        </p>
        <Button variant="outline" onClick={handleReset} data-testid="new-application-btn">
          Yeni mesaj göndər
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form" noValidate>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="firstName">Ad *</Label>
          <Input id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} required placeholder="Adınız" data-testid="contact-first-name-input" className={errors.firstName ? 'border-red-500' : ''} />
          {errors.firstName && <p className="text-xs text-red-500 mt-1">{errors.firstName}</p>}
        </div>
        <div>
          <Label htmlFor="lastName">Soyad *</Label>
          <Input id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} required placeholder="Soyadınız" data-testid="contact-last-name-input" className={errors.lastName ? 'border-red-500' : ''} />
          {errors.lastName && <p className="text-xs text-red-500 mt-1">{errors.lastName}</p>}
        </div>
      </div>

      <div>
        <Label htmlFor="email">Elektron ünvan *</Label>
        <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required placeholder="ad@nümunə.az" data-testid="contact-email-input" className={errors.email ? 'border-red-500' : ''} autoComplete="email" />
        {errors.email ? (
          <p className="text-xs text-red-500 mt-1">{errors.email}</p>
        ) : (
          <p className="text-xs text-gray-500 mt-1">Cavabımızı bu ünvana göndərəcəyik</p>
        )}
      </div>

      <div>
        <Label htmlFor="message">Sualınız / Mesajınız *</Label>
        <Textarea id="message" name="message" value={formData.message} onChange={handleChange} required placeholder="Sualınızı və ya əlaqə saxlamaq istədiyiniz məsələni qısaca yazın..." rows={5} data-testid="contact-message-input" className={errors.message ? 'border-red-500' : ''} />
        {errors.message && <p className="text-xs text-red-500 mt-1">{errors.message}</p>}
      </div>

      <div className="pt-2">
        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox
            checked={consent}
            onCheckedChange={(v) => {
              setConsent(!!v);
              if (errors.consent) setErrors({ ...errors, consent: '' });
            }}
            className="mt-0.5"
            data-testid="contact-consent-checkbox"
          />
          <span className="text-sm text-gray-600 leading-relaxed">
            <Link to="/mexfilik-siyaseti" className="text-blue-700 hover:underline" target="_blank">
              Məxfilik siyasəti
            </Link>
            {' '}və{' '}
            <Link to="/istifade-shertleri" className="text-blue-700 hover:underline" target="_blank">
              İstifadə şərtləri
            </Link>
            {' '}ilə tanış oldum və şəxsi məlumatlarımın emalına razılıq verirəm.
          </span>
        </label>
        {errors.consent && <p className="text-xs text-red-500 mt-2 ml-7">{errors.consent}</p>}
      </div>

      <Button type="submit" className="w-full bg-blue-700 hover:bg-blue-800 text-white" size="lg" disabled={isSubmitting} data-testid="contact-submit-btn">
        {isSubmitting ? (
          <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Göndərilir...</>
        ) : (
          <><Send className="mr-2 h-5 w-5" />Mesajı göndər</>
        )}
      </Button>
    </form>
  );
};
